import axios from 'axios'
import { invalidateAllianceAreasListCache } from '../game/world-map/worldMapApi'
import { getSharedPvPSocket } from '../socket/pvpSharedSocket'

/** Membership/alliance lifecycle mutates territory lists server-side — bypass alliance-area SPA TTL on next fetch. */
function invalidateAllianceAreasAfterRosterMutation() {
  invalidateAllianceAreasListCache()
}

const ALLIANCE_FLAG_BASE =
  'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/land/alliance'

/** Keep in sync with `eclipsera_backend/constants/allianceFlags.js` */
export const ALLIANCE_FLAG_COUNT = 285

export const ALLIANCE_FLAG_URLS: readonly string[] = Object.freeze(
  Array.from(
    { length: ALLIANCE_FLAG_COUNT },
    (_, i) => `${ALLIANCE_FLAG_BASE}/alliance-flag-${i + 1}.png`,
  ),
)

/**
 * Fire off plain `Image()` preloads for every alliance flag PNG so the picker (285 thumbnails)
 * renders from cache the moment the alliance page opens. Browser caches respond; subsequent
 * `<img src>` reuses the decoded entry. Idempotent — only runs once per session.
 *
 * Wired into the idle-warmup pipeline (`warmupAssets.ts`) so this runs during browser idle time
 * AFTER the land LCP image paints, not during cold boot.
 */
let allianceFlagPreloadStarted = false
export function preloadAllianceFlagAssets(): void {
  if (allianceFlagPreloadStarted) return
  if (typeof window === 'undefined' || typeof Image === 'undefined') return
  allianceFlagPreloadStarted = true
  for (const url of ALLIANCE_FLAG_URLS) {
    try {
      const img = new Image()
      img.decoding = 'async'
      img.loading = 'eager'
      img.src = url
    } catch {
      /* ignore — best effort warmup */
    }
  }
}

// ── Types ──

/** Leader is always `sovereign`; others use assignable ranks from the alliance leader. */
export type AllianceAssignableRank = 'normal' | 'regent' | 'aegis' | 'nexus' | 'accord'
export type AllianceMemberRank = 'sovereign' | AllianceAssignableRank

export const ALLIANCE_ASSIGNABLE_RANKS: readonly AllianceAssignableRank[] = [
  'normal',
  'regent',
  'aegis',
  'nexus',
  'accord',
]

/** Minimum account level required to hold each alliance position. */
export const ALLIANCE_RANK_MIN_LEVEL: Readonly<Record<AllianceMemberRank, number>> = Object.freeze({
  sovereign: 5,
  regent: 5,
  aegis: 4,
  nexus: 4,
  accord: 4,
  normal: 3,
})

export function allianceRankTranslationKey(rank: string): `alliance.${string}` {
  const r = (rank || 'normal').toLowerCase()
  const key: Record<string, `alliance.${string}`> = {
    sovereign: 'alliance.rankSovereign',
    normal: 'alliance.rankNormal',
    regent: 'alliance.rankRegent',
    aegis: 'alliance.rankAegis',
    nexus: 'alliance.rankNexus',
    accord: 'alliance.rankAccord',
  }
  return key[r] || 'alliance.rankNormal'
}

/** For `useTranslation('translation', { keyPrefix: 'alliance' })` — key path inside the `alliance` object. */
export function allianceRankKeyRelativeToAlliance(rank: string): string {
  return allianceRankTranslationKey(rank).replace(/^alliance\./, '')
}

/** Leader is always Sovereign (even if `rank` is missing). Non-leaders default missing/invalid stored rank to Normal user (`normal`). */
export function displayAllianceMemberRank(member: {
  isLeader?: boolean
  rank?: AllianceMemberRank | string
}): AllianceMemberRank {
  if (member.isLeader) return 'sovereign'
  const r = String(member.rank || '').toLowerCase()
  if ((ALLIANCE_ASSIGNABLE_RANKS as readonly string[]).includes(r)) return r as AllianceAssignableRank
  return 'normal'
}

export interface AllianceMemberDetail {
  walletAddress: string
  nickName: string
  avatarUrl?: string | null
  userLvl: number
  isLeader: boolean
  rank: AllianceMemberRank
  /** From user presence (socket / session). */
  isOnline?: boolean
  /** Tab visibility from the member's last heartbeat (false = online but backgrounded); null when unknown. */
  isVisible?: boolean | null
  /** ISO timestamp of last activity when offline tracking applies. */
  lastSeen?: string | null
  /** Member's own KPS (Kingdom Power Score) — Management tab "Power" column. */
  power?: number
  /** Lifetime resources contributed to the alliance treasury (auto-tax) — Management tab "Contribution" column. */
  contribution?: number
}

export interface AllianceBonuses {
  production: Record<string, number>
  production_flat?: Record<string, number>
  production_all: number
  stat: Record<string, number>
  stat_flat?: Record<string, number>
  time_reduction: Record<string, number>
  trade_discount: number
  cost_reduction?: Record<string, number>
  mvm_march?: number
  world_map_march_time_pct?: number
  building_hp_flat?: number
}

export interface AllianceResources {
  ecs: number
  water: number
  wheat: number
  bread: number
  wood: number
  stone: number
  iron: number
}

/** Current alliance task period (overview) — slim payload from GET /alliance/overview. */
export interface AllianceTaskPeriodSummary {
  periodStart: string
  periodEnd: string
  status: 'voting' | 'awaiting_leader' | 'finalized' | 'expired'
  officialOptionId: string | null
  officialOptionLabel: string | null
  awaitingLeaderFinalize?: boolean
}

export interface AllianceOverview {
  myAlliance: {
    _id: string
    leaderWalletAddress: string
    leaderNickName: string
    leaderAvatarUrl?: string | null
    leaderLevel: number
    name?: string | null
    flagUrl?: string | null
    description?: string
    members: string[]
    memberCount: number
    maxMembers: number
    membersDetailed: AllianceMemberDetail[]
    resources: AllianceResources
    completedTechs: string[]
    currentResearch: {
      techId: string | null
      startedAt: string | null
      completesAt: string | null
    }
    contributionRate: number
    bonuses: AllianceBonuses
    diplomacyScore: number
    /** Set when a task period is active (voting or finalized) for the alliance. */
    taskPeriod?: AllianceTaskPeriodSummary | null
    /** ISO timestamp — alliance document creation (Mongoose `timestamps`). */
    createdAt?: string
    /** Alliance leveling — `xp` is progress within the CURRENT level (resets on level-up), not cumulative. */
    level: number
    xp: number
    /** XP needed to advance from `level` to `level + 1` — render `xp / nextLevelXp` as the progress bar. */
    nextLevelXp: number
    /** Sum of every member's (+ leader's) KPS (Kingdom Power Score) — "Alliance Power". */
    powerScore: number
    /** Alliance MvM record — Σ member wins / losses (leader included); `mvmTotal` is battles fought. */
    mvmWins?: number
    mvmLosses?: number
    mvmTotal?: number
    /** 'open' allows instant join-direct; 'invite_only' requires an invite or leader-approved join-request. */
    joinPolicy: 'open' | 'invite_only'
    /** Latest activity feed entries (Overview tab "Alliance Activity"), newest first. */
    recentActivity: { type: string; message: string; createdAt: string }[]
  } | null
  pendingInvites: {
    _id: string
    allianceId: string
    allianceName?: string | null
    flagUrl?: string | null
    inviterWalletAddress: string
    inviterNickName: string
    inviterAvatarUrl?: string | null
  }[]
  invitableUsers: {
    walletAddress: string
    nickName: string
    avatarUrl?: string | null
    userLvl: number
    canInvite: boolean
    /** Player diplomacy with leader — invite UI only shows ceasefire+ */
    diplomacyLevel?: 'ceasefire' | 'alliance' | 'marriage' | null
    isOnline?: boolean
    /** A pending invite to this alliance already exists — Invite UI disables the button rather than hiding the row. */
    alreadyInvited?: boolean
  }[]
  canCreate: boolean
  userLevel: number
  empirePeriodId: number
  maxMembers: number
  /** Leader only: pending invites for this alliance */
  pendingOutgoingInvites?: number
  /** Leader only: cap on pending invites from `getAllianceOutgoingInviteLimit` */
  maxOutgoingInvites?: number

  /** Pending leadership duel (low governance challenge). */
  leadershipChallenge?: {
    challengeId: string
    status: 'pending_accept' | 'battle_pending' | string
    challengerWallet: string
    leaderWallet: string
    stakeEcs?: number
    pvpRoomId?: string | null
  } | null
  /** Member (non-leader): true when governance is low, enough ECS, leader is live on socket, no pending challenge, and Dictator punishment research is complete. */
  canChallengeForLeadership?: boolean
  /** Same preconditions as `canChallengeForLeadership` but player has not researched Kingdom "Dictator punishment unlocked" — show locked CTA. */
  leadershipChallengeNeedsResearch?: boolean
  /**
   * Every flag URL currently claimed by an alliance. Pickers grey these out so users can't pick
   * a flag that's already taken. The leader's settings modal excludes its own current flag before
   * applying the disable. Server-side `flagInUse` check is the authoritative backstop against
   * stale-cache races (overview cache TTL is ~15 s).
   */
  takenFlagUrls?: string[]
}

export interface TechBuildingBonus {
  type: string
  resource?: string
  stat?: string
  action?: string
  percent?: number
  percentPerLevel?: number
  resources?: string[]
  resourceA?: string
  resourceB?: string
  category?: string
  flatPerLevel?: number
}

/** Alliance technology building (per-level upgrades; `level` = current building tier). */
export interface TechNode {
  id: string
  sortIndex?: number
  branch: string
  name: string
  shortLabel?: string
  description: string
  prerequisites: string[]
  cost: Record<string, number>
  researchTime: number
  bonus: TechBuildingBonus
  /** Current upgrade level (0 = not started). */
  level: number
  maxLevel?: number
  isMaxed?: boolean
  isCompleted: boolean
  isResearching: boolean
  canResearch: boolean
  researchComplete: boolean
}

export interface TechTreeResponse {
  techTree: Record<string, TechNode>
  /** Ordered 1…14; same as techTree values, sorted. */
  buildings?: TechNode[]
  currentResearch: {
    techId: string
    startedAt: string
    completesAt: string
    tech: TechNode | null
    isComplete: boolean
  } | null
  completedTechs: string[]
  /** Per-building levels (alliance treasury tech). */
  techBuildingLevels?: Record<string, number>
  bonuses: AllianceBonuses
  allianceResources: AllianceResources
  isLeader: boolean
}

export interface AllianceMessage {
  _id: string
  allianceId: string
  senderWalletAddress: string
  senderNickName: string
  senderAvatarUrl?: string | null
  title: string
  message: string
  resourceRequest?: AllianceResources
  isResourceRequest: boolean
  readBy: string[]
  createdAt: string
}

export interface AllianceDiplomacyRelation {
  _id: string
  allianceA: string
  allianceB: string
  points: number
  level: string
  otherAllianceName: string
  otherAllianceFlagUrl?: string | null
  /** Convenience id of the counterparty alliance (not yours). */
  otherAllianceId?: string
  otherAlliancePowerScore?: number
  otherLeaderNickName?: string
  lastInteractionAt?: string | null
}

/** Gift/request milestones returned with GET diplomacy (sorted newest first). */
export interface AllianceDiplomacyActivity {
  id: string
  at: string
  kind: string
  label: string
}

export interface AllianceDiplomacyGift {
  _id: string
  fromAllianceId: string
  toAllianceId: string
  resources: AllianceResources
  pointsAwarded: number
  status: string
}

/** Envoy / tribute mission between alliances (server-driven phases). */
export interface AllianceDiplomacyMissionRow {
  _id: string
  fromAllianceId: string
  toAllianceId: string
  characterInstanceId: string
  envoyCharacterName?: string | null
  phase: string
  hexSteps: number
  legTravelMs: number
  legDepartedAt: string
  legArrivesAt: string
  tributePointsRequested?: number | null
  tributeResourcesSnapshot?: Partial<AllianceResources> | null
  acceptedPeaceUpgrade?: boolean
  otherAllianceId: string
  otherAllianceName: string
  otherAllianceFlagUrl?: string | null
  isOutgoing: boolean
}

export interface AllianceDiplomacyOverview {
  relationships: AllianceDiplomacyRelation[]
  pendingRequests: any[]
  pendingGifts: AllianceDiplomacyGift[]
  recentActivities?: AllianceDiplomacyActivity[]
  diplomacyMissions?: AllianceDiplomacyMissionRow[]
  isLeader: boolean
  allianceResources: AllianceResources
  allianceId: string
}

export interface ContributionLog {
  _id: string
  allianceId: string
  walletAddress: string
  date: string
  rate: number
  contributed: AllianceResources
}

// ── Core API ──

/** Match `ALLIANCE_LEADERSHIP_CHALLENGE_STAKE_ECS` / threshold on the server. */
export const ALLIANCE_LEADERSHIP_CHALLENGE_STAKE_ECS = 500
export const ALLIANCE_LEADERSHIP_CHALLENGE_GOVERNANCE_THRESHOLD = 50

/** Match server `ALLIANCE_LEADERSHIP_CHALLENGE_SOCKET_EVENT` — push when challenge created / declined / accepted. */
export const ALLIANCE_LEADERSHIP_CHALLENGE_UPDATE_EVENT = 'alliance:leadership-challenge-update'

export const getAllianceOverview = async (walletAddress: string) => {
  const res = await axios.get(`/alliance/overview/${walletAddress}`)
  return res.data
}

const SOCKET_ACK_MS = 45000

async function ensureSocketConnected (socket: import('socket.io-client').Socket): Promise<void> {
  if (socket.connected) return
  await new Promise<void>((resolve, reject) => {
    const t = window.setTimeout(() => reject(new Error('Socket connection timeout')), SOCKET_ACK_MS)
    socket.once('connect', () => {
      window.clearTimeout(t)
      resolve()
    })
    socket.once('connect_error', (e) => {
      window.clearTimeout(t)
      reject(e)
    })
  })
}

function emitWithAck<T extends Record<string, unknown>> (
  socket: import('socket.io-client').Socket,
  event: string,
  payload: object,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = window.setTimeout(() => reject(new Error('Request timed out')), SOCKET_ACK_MS)
    socket.emit(event, payload, (res: T) => {
      window.clearTimeout(t)
      resolve(res)
    })
  })
}

/** Leadership challenge flow uses Socket.IO (leader must be online). */
export const requestAllianceLeadershipChallenge = async (walletAddress: string) => {
  const socket = getSharedPvPSocket(walletAddress)
  await ensureSocketConnected(socket)
  const res = await emitWithAck<{
    success?: boolean
    message?: string
    challenge?: unknown
  }>(socket, 'alliance:leadership-challenge-request', { walletAddress })
  return res
}

/** Check if a wallet is in an alliance (for attack eligibility on world map) */
export const checkIsWalletInAlliance = async (walletAddress: string): Promise<boolean> => {
  const res = await axios.get(`/alliance/is-member/${encodeURIComponent(walletAddress)}`)
  return res.data?.inAlliance === true
}

/**
 * Membership plus whether the wallet leads the alliance, from the same cheap endpoint — for
 * callers that need to tell leader from member (the land HUD badge) without pulling the full
 * `/alliance/overview` payload.
 */
export const getAllianceMembershipRole = async (
  walletAddress: string,
): Promise<{ inAlliance: boolean; isLeader: boolean }> => {
  const res = await axios.get(`/alliance/is-member/${encodeURIComponent(walletAddress)}`)
  return {
    inAlliance: res.data?.inAlliance === true,
    isLeader: res.data?.isLeader === true,
  }
}

export const respondAllianceLeadershipChallenge = async (
  walletAddress: string,
  challengeId: string,
  accept: boolean,
) => {
  const socket = getSharedPvPSocket(walletAddress)
  await ensureSocketConnected(socket)
  const res = await emitWithAck<{
    success?: boolean
    message?: string
    roomId?: string
    challenge?: unknown
    declined?: boolean
  }>(socket, 'alliance:leadership-challenge-respond', {
    walletAddress,
    challengeId,
    accept,
  })
  return res
}

/** Pick 3 heroes for alliance leadership PvP room; may return battleId when both sides are ready (socket). */
export const selectAllianceLeadershipBattleCharacters = async (
  walletAddress: string,
  roomId: string,
  characterInstanceIds: string[],
) => {
  const socket = getSharedPvPSocket(walletAddress)
  await ensureSocketConnected(socket)
  const res = await emitWithAck<{
    success?: boolean
    message?: string
    roomId?: string
    battleStarted?: boolean
    battleId?: string
    error?: string
  }>(socket, 'pvp:alliance-leadership-select-characters', {
    walletAddress,
    roomId,
    characterInstanceIds,
  })
  return res
}

export const createAlliance = async (
  walletAddress: string,
  name?: string,
  flagUrl?: string,
  options?: { motto?: string; joinPolicy?: 'open' | 'invite_only' },
) => {
  const res = await axios.post('/alliance/create', {
    walletAddress,
    name,
    flagUrl,
    motto: options?.motto,
    joinPolicy: options?.joinPolicy,
  })
  invalidateAllianceAreasAfterRosterMutation()
  return res.data
}

export const inviteToAlliance = async (walletAddress: string, targetWalletAddress: string) => {
  const res = await axios.post('/alliance/invite', { walletAddress, targetWalletAddress })
  return res.data
}

export const acceptAllianceInvite = async (walletAddress: string, inviteId: string) => {
  const res = await axios.post('/alliance/accept-invite', { walletAddress, inviteId })
  invalidateAllianceAreasAfterRosterMutation()
  return res.data
}

export const ignoreAllianceInvite = async (walletAddress: string, inviteId: string) => {
  const res = await axios.post('/alliance/ignore-invite', { walletAddress, inviteId })
  return res.data
}

export interface AllianceOutgoingInviteItem {
  _id: string
  inviteeWalletAddress: string
  inviteeNickName: string
  inviteeLevel: number
  inviteeAvatarUrl: string | null
  createdAt: string
}

/** Pending invites sent out by the caller's alliance (leader only; empty when not a leader). */
export const getOutgoingAllianceInvites = async (walletAddress: string) => {
  const res = await axios.get(`/alliance/outgoing-invites/${encodeURIComponent(walletAddress)}`)
  return res.data as {
    isLeader: boolean
    invites: AllianceOutgoingInviteItem[]
  }
}

/** Leader withdraws a pending invite before it's accepted/ignored. */
export const cancelAllianceInvite = async (walletAddress: string, inviteId: string) => {
  const res = await axios.post('/alliance/cancel-invite', { walletAddress, inviteId })
  return res.data
}

export const leaveAlliance = async (walletAddress: string) => {
  const res = await axios.post('/alliance/leave', { walletAddress })
  invalidateAllianceAreasAfterRosterMutation()
  return res.data
}

export const updateAllianceSettings = async (walletAddress: string, settings: {
  contributionRate?: number
  description?: string
  name?: string
  flagUrl?: string
  joinPolicy?: 'open' | 'invite_only'
}) => {
  const res = await axios.post('/alliance/settings', { walletAddress, ...settings })
  return res.data
}

export const kickMember = async (walletAddress: string, targetWalletAddress: string) => {
  const res = await axios.post('/alliance/kick', { walletAddress, targetWalletAddress })
  invalidateAllianceAreasAfterRosterMutation()
  return res.data
}

/** Leader only. `rank` must be normal, regent, aegis, nexus, or accord — never sovereign. */
export const setAllianceMemberRank = async (
  walletAddress: string,
  targetWalletAddress: string,
  rank: AllianceAssignableRank,
) => {
  const res = await axios.post('/alliance/member-rank', { walletAddress, targetWalletAddress, rank })
  return res.data
}

/** Leader only: delete alliance, remove all members, clean related data. */
export const dismantleAlliance = async (walletAddress: string) => {
  const res = await axios.post('/alliance/dismantle', { walletAddress })
  invalidateAllianceAreasAfterRosterMutation()
  return res.data
}

export const donateToAlliance = async (walletAddress: string, resources: Partial<AllianceResources>) => {
  const res = await axios.post('/alliance/donate', { walletAddress, resources })
  return res.data
}

export interface DonateToMemberPayload {
  resources?: Partial<AllianceResources>
  weapons?: Array<{ craftedWeaponId: string; count?: number } | string>
}

export const donateToMember = async (
  walletAddress: string,
  targetWalletAddress: string,
  payload: DonateToMemberPayload
) => {
  const res = await axios.post('/alliance/donate-member', {
    walletAddress,
    targetWalletAddress,
    ...payload,
  })
  return res.data
}

export const getContributionHistory = async (walletAddress: string) => {
  const res = await axios.get(`/alliance/contributions/${walletAddress}`)
  return res.data
}

// ── Tech Tree ──

export const getTechTree = async (walletAddress: string): Promise<TechTreeResponse> => {
  const res = await axios.get(`/alliance/tech/${walletAddress}`)
  return res.data
}

export const startResearch = async (walletAddress: string, techId: string) => {
  const res = await axios.post('/alliance/tech/start', { walletAddress, techId })
  return res.data
}

export const completeResearch = async (walletAddress: string) => {
  const res = await axios.post('/alliance/tech/complete', { walletAddress })
  return res.data
}

// ── Messages ──

export const getAllianceMessages = async (walletAddress: string) => {
  const res = await axios.get(`/alliance/messages/${walletAddress}`)
  return res.data
}

export const sendAllianceMessage = async (walletAddress: string, data: {
  title?: string
  message: string
  resourceRequest?: Partial<AllianceResources>
  isResourceRequest?: boolean
}) => {
  const res = await axios.post('/alliance/messages/send', { walletAddress, ...data })
  return res.data
}

// ── Alliance Diplomacy ──

export const getAllianceDiplomacy = async (walletAddress: string): Promise<AllianceDiplomacyOverview> => {
  const res = await axios.get(`/alliance/diplomacy/${walletAddress}`)
  return res.data
}

export interface ListAllianceItem {
  _id: string
  name: string
  flagUrl?: string | null
  leaderWalletAddress?: string
  leaderNickName?: string
  memberCount: number
  maxMembers: number
  /** Strategic region the alliance is homed in (leader castle's region); joiners relocate there. */
  regionGroupId?: string | null
  /** Alliance type: 'open' takes join requests, 'invite_only' is leader-invite only. */
  joinPolicy?: 'open' | 'invite_only'
  level?: number
  powerScore?: number
  isMyAlliance: boolean
}

export const listAlliances = async (walletAddress: string): Promise<{ alliances: ListAllianceItem[] }> => {
  const res = await axios.get(`/alliance/diplomacy/list/${walletAddress}`)
  return res.data
}

// ── Alliance join requests (request → leader approves/declines) ──
//
// There is no direct-join call: `/alliance/join-direct` was removed server-side so membership
// can only be written by a leader approving a request (or by accepting an invite).

/** Send a join request to an alliance's leader. */
export const sendAllianceJoinRequest = async (walletAddress: string, allianceId: string) => {
  const res = await axios.post('/alliance/join-request', { walletAddress, allianceId })
  return res.data as { success?: boolean; requestId?: string; message?: string }
}

export interface MyAllianceJoinRequest {
  requestId: string
  allianceId: string
}

/**
 * Every join request the caller is still waiting on — one per alliance, several alliances at
 * once. The alliance list disables Join for each of these, and the server rejects a second
 * request to an alliance already in this list.
 */
export const getMyAllianceJoinRequests = async (walletAddress: string) => {
  const res = await axios.get(`/alliance/join-request/mine/${encodeURIComponent(walletAddress)}`)
  const data = res.data as {
    pending?: MyAllianceJoinRequest[]
    pendingAllianceIds?: string[]
    latest?: MyAllianceJoinRequest | null
  }
  const pending = data?.pending || []
  return {
    pending,
    pendingAllianceIds: data?.pendingAllianceIds || pending.map((p) => p.allianceId),
  }
}

export interface AllianceJoinRequestItem {
  _id: string
  requesterWalletAddress: string
  requesterNickName: string
  requesterLevel: number
  requesterKps: number
  /** Alliance-task completion score (0–100). */
  requesterCompletionPercent: number
  requesterAvatarUrl: string | null
  createdAt: string
}

/** Pending join requests for the caller's alliance (leader only; empty when not a leader). */
export const getAllianceJoinRequests = async (walletAddress: string) => {
  const res = await axios.get(`/alliance/join-requests/${encodeURIComponent(walletAddress)}`)
  return res.data as {
    isLeader: boolean
    allianceId: string | null
    allianceName?: string
    requests: AllianceJoinRequestItem[]
  }
}

/** Leader approves a join request → the requester becomes a member. */
export const approveAllianceJoinRequest = async (walletAddress: string, requestId: string) => {
  const res = await axios.post('/alliance/join-request/approve', { walletAddress, requestId })
  invalidateAllianceAreasAfterRosterMutation()
  return res.data
}

/** Leader declines a join request. */
export const declineAllianceJoinRequest = async (walletAddress: string, requestId: string) => {
  const res = await axios.post('/alliance/join-request/decline', { walletAddress, requestId })
  return res.data
}

// ── Post-victory castle Conquer vs Raid ──

/** Fetch the winner's most-recent still-open conquer offer (socket-race fallback). */
export const getMyCastleConquerOffer = async (walletAddress: string) => {
  const res = await axios.get(`/alliance/castle-conquer/my-offer/${encodeURIComponent(walletAddress)}`)
  return res.data as { offer: { offerId: string; allianceName: string | null; battleId: string | null } | null }
}

/** Winner keeps the raid loot (dismiss the conquer offer). */
export const chooseCastleRaid = async (walletAddress: string, offerId: string) => {
  const res = await axios.post('/alliance/castle-conquer/raid', { walletAddress, offerId })
  return res.data
}

/**
 * Winner chooses Conquer & Recruit — recruiting only, never resources. A leader recruits the castle
 * immediately (`autoApproved`); anyone else asks their leader to accept (`pending`). When recruiting
 * is impossible (alliance full, or no alliance) nothing happens at all: `recruitFailed`, the plunder
 * is returned to the defender and the winner keeps only the battle XP.
 */
export const chooseCastleConquer = async (walletAddress: string, offerId: string) => {
  const res = await axios.post('/alliance/castle-conquer/request', { walletAddress, offerId })
  invalidateAllianceAreasAfterRosterMutation()
  return res.data as {
    success?: boolean
    autoApproved?: boolean
    pending?: boolean
    recruitFailed?: boolean
    reason?: string
    message?: string
  }
}

export interface CastleConquerRequestItem {
  _id: string
  requesterWalletAddress: string
  requesterNickName: string
  requesterLevel: number
  requesterKps: number
  requesterCompletionPercent: number
  requesterAvatarUrl: string | null
  defeatedOwnerName: string
  createdAt: string
}

/** Pending castle-conquer requests for the caller's alliance (leader only). */
export const getCastleConquerRequests = async (walletAddress: string) => {
  const res = await axios.get(`/alliance/castle-conquer/requests/${encodeURIComponent(walletAddress)}`)
  return res.data as {
    isLeader: boolean
    allianceId: string | null
    allianceName?: string
    requests: CastleConquerRequestItem[]
  }
}

/**
 * Leader approves a conquer request → the castle joins the alliance and the raid loot is reversed.
 * `recruitFailed` means there was no room after all: the request is closed, the plunder is returned
 * and nothing changes.
 */
export const approveCastleConquerRequest = async (walletAddress: string, requestId: string) => {
  const res = await axios.post('/alliance/castle-conquer/approve', { walletAddress, requestId })
  invalidateAllianceAreasAfterRosterMutation()
  return res.data as { success?: boolean; recruitFailed?: boolean; reason?: string; message?: string }
}

/** Leader declines a conquer request (winner keeps the raid loot). */
export const declineCastleConquerRequest = async (walletAddress: string, requestId: string) => {
  const res = await axios.post('/alliance/castle-conquer/decline', { walletAddress, requestId })
  return res.data
}

// ── Alliance weekly task vote (fixed 5 strategic options + governance) ──

/** The 5 fixed strategic options members vote on each week (Tuesday→Tuesday). */
export type AllianceTaskOptionId = 'aps' | 'conquer' | 'raid' | 'diplomacy' | 'technology'

export interface AllianceTaskOptionClient {
  id: AllianceTaskOptionId
  label: string
  votes: number
  /** 0 = most-voted this season. */
  rank: number
}

export interface AllianceTaskPeriodClient {
  _id: string
  seasonKey: string
  /** Monday 00:00 UTC. */
  periodStart: string
  /** Tuesday 00:00 UTC — voting (Monday only) closes here. */
  votingEndsAt: string
  /** Wednesday 00:00 UTC — leader's deadline to set the task; miss it → no task + governance −20. */
  finalizeEndsAt: string
  /** Saturday 00:00 UTC — season runs Monday…Friday; weekends excluded. */
  periodEnd: string
  status: 'voting' | 'awaiting_leader' | 'finalized' | 'expired'
  officialOptionId: AllianceTaskOptionId | null
  officialOptionLabel: string | null
  /** APS official option only: committed growth target (10 / 20 / 30). */
  apsTargetPercent: number | null
  /** Legacy diplomacy field (old level 1 / 2 / 3). Unused by the new flow. */
  diplomacyLevel: number | null
  /** Diplomacy official option only: how many alliances (1 / 2 / 3) + per-alliance target tiers. */
  diplomacyCount?: number | null
  diplomacyTargets?: DiplomacyTargetClient[]
  /** Technology official option only: the tech building to advance + how many times. */
  techBuildingId: string | null
  techBuildingLabel: string | null
  techAdvanceTimes: number | null
  techBaselineLevel: number | null
  /** Conquer/Raid official option only: castle count + scheduled targets. */
  attackCount: number | null
  attackTargets: AttackTargetClient[]
  /** Whole-task completion (scored at season end / by the battle hook). */
  completed: boolean
  /** Live progress toward the official task (null while voting / no task set). */
  progress?: AllianceTaskProgress | null
  /** APS official option only (finalized): per-member KPS breakdown for the growth table. */
  apsMembers?: AllianceApsMemberProgress[]
  governanceDeltaApplied: number
  totalVotes: number
  options: AllianceTaskOptionClient[]
  /** The viewing member's current vote (null if not cast). */
  myVote: AllianceTaskOptionId | null
}

/** One member's contribution to an active APS ("grow your KPS") task. */
export interface AllianceApsMemberProgress {
  walletAddress: string
  nickName: string
  avatarUrl?: string | null
  isLeader?: boolean
  /** KPS snapshot at task lock; null if the member joined after finalize (not scored). */
  baselineKps: number | null
  /** Live KPS now. */
  currentKps: number
  /** Personal target = baseline × (1 + target%); null when not tracked. */
  targetKps: number | null
  /** KPS still needed for the personal target (baseline × target%); null when not tracked. */
  neededKps: number | null
  /** KPS gained since baseline (>= 0); null when not tracked. */
  gainedKps: number | null
  /** Personal completion % — 100 = grew by the target, UNCAPPED above (150/200…); null when not tracked. */
  percent: number | null
  done: boolean
  /** True when the member was present at finalize (counts toward scoring). */
  tracked: boolean
}

export interface AttackTargetClient {
  targetAllianceId: string | null
  targetMemberWallet: string
  attackerWallets: string[]
  mainAttackerWallet: string | null
  attackFrom: string
  attackTo: string
  completed: boolean
  /** Resolved display names / castle info (attached server-side). */
  targetNickName?: string
  targetAvatarUrl?: string | null
  mainAttackerNickName?: string
  /** Defender castle map position (hex q/r) + level, resolved server-side. */
  coordinates?: { q: number; r: number } | null
  castleLevel?: number | null
  attackers?: { walletAddress: string; nickName: string; avatarUrl?: string | null; isMain: boolean }[]
}

/** Payload the leader submits per castle when locking a Conquer or Raid task. */
export interface AttackTargetInput {
  targetAllianceId: string
  targetMemberWallet: string
  attackerWallets: string[]
  mainAttackerWallet: string
  attackFrom: string
  attackTo: string
}

/** Relationship tier reachable as a diplomacy-task target (spirit is the implicit floor). */
export type AllianceDiplomacyTargetLevel = 'ceasefire' | 'alliance' | 'marriage'

/** Payload the leader submits per alliance when locking a Diplomacy task. */
export interface DiplomacyTargetInput {
  targetAllianceId: string
  targetDiplomacyLevel: AllianceDiplomacyTargetLevel
  from: string
  to: string
}

/** Server view of one diplomacy target (name + current tier attached server-side). */
export interface DiplomacyTargetClient {
  targetAllianceId: string | null
  targetDiplomacyLevel: AllianceDiplomacyTargetLevel
  from: string
  to: string
  completed: boolean
  targetAllianceName?: string
  targetAllianceFlagUrl?: string | null
  /** Current relationship tier with this alliance (spirit / ceasefire / alliance / marriage). */
  currentLevel?: string
}

export interface AllianceWarTargetMember {
  walletAddress: string
  nickName: string
  userLvl: number
  kps?: number
  avatarUrl?: string | null
  isLeader?: boolean
}

/** Live progress toward the finalized official task (null until one is set). */
export interface AllianceTaskProgress {
  kind: AllianceTaskOptionId
  baseline: number
  current: number
  target: number
  /** 0–100. */
  percent: number
  done: boolean
  unit: 'castles' | 'levels' | 'aps' | 'gifts'
}

export interface AllianceTaskStatus {
  success: boolean
  allianceId?: string
  isLeader: boolean
  taskPeriod: AllianceTaskPeriodClient | null
  needsVote: boolean
  needsLeaderFinalize: boolean
  finalizePeriod: AllianceTaskPeriodClient | null
}

export const getAllianceWarTargetMembers = async (walletAddress: string, allianceId: string) => {
  const res = await axios.get(
    `/alliance/tasks/target-members/${encodeURIComponent(walletAddress)}/${encodeURIComponent(allianceId)}`,
  )
  return res.data as { members: AllianceWarTargetMember[] }
}

export const getAllianceTaskPeriod = async (walletAddress: string) => {
  const res = await axios.get(`/alliance/tasks/${encodeURIComponent(walletAddress)}`)
  return res.data as AllianceTaskStatus
}

/** Lightweight gating fetch for the global watcher (same payload as getAllianceTaskPeriod). */
export const getAllianceTaskStatus = async (walletAddress: string) => {
  const res = await axios.get(`/alliance/tasks/status/${encodeURIComponent(walletAddress)}`)
  return res.data as AllianceTaskStatus
}

export const submitAllianceTaskVote = async (walletAddress: string, optionId: AllianceTaskOptionId) => {
  const res = await axios.post('/alliance/tasks/vote', { walletAddress, optionId })
  return res.data as { success: boolean; taskPeriod: AllianceTaskPeriodClient; message?: string }
}

export interface FinalizeTaskExtras {
  apsTargetPercent?: number
  diplomacyLevel?: number
  diplomacyCount?: number
  diplomacyTargets?: DiplomacyTargetInput[]
  techBuildingId?: string
  techAdvanceTimes?: number
  attackCount?: number
  attackTargets?: AttackTargetInput[]
}

export const finalizeAllianceTaskPeriod = async (
  walletAddress: string,
  optionId: AllianceTaskOptionId,
  periodId?: string,
  extras?: FinalizeTaskExtras,
) => {
  const res = await axios.post('/alliance/tasks/finalize', {
    walletAddress,
    optionId,
    periodId,
    ...extras,
  })
  return res.data as {
    success: boolean
    taskPeriod: AllianceTaskPeriodClient
    governanceScore?: number
    governanceDelta?: number
    governanceTier?: 'top' | 'mid' | 'low' | null
    apsTargetPercent?: number | null
    diplomacyLevel?: number | null
    attackCount?: number | null
    techBuildingId?: string | null
    techAdvanceTimes?: number | null
    message?: string
  }
}

export const sendAllianceGift = async (walletAddress: string, targetAllianceId: string, resources: Partial<AllianceResources>) => {
  const res = await axios.post('/alliance/diplomacy/gift', { walletAddress, targetAllianceId, resources })
  return res.data
}

export const receiveAllianceGift = async (walletAddress: string, giftId: string) => {
  const res = await axios.post('/alliance/diplomacy/gift/receive', { walletAddress, giftId })
  return res.data
}

export const sendAllianceDiplomacyRequest = async (walletAddress: string, targetAllianceId: string, requestType: string) => {
  const res = await axios.post('/alliance/diplomacy/request', { walletAddress, targetAllianceId, requestType })
  return res.data
}

export const handleAllianceDiplomacyRequest = async (walletAddress: string, requestId: string, action: 'accept' | 'ignore') => {
  const res = await axios.post('/alliance/diplomacy/request/handle', { walletAddress, requestId, action })
  return res.data
}

/** Leader only: removes the diplomacy edge and clears pending gifts/requests between the two alliances. */
export const breakAllianceDiplomacyRelation = async (walletAddress: string, targetAllianceId: string) => {
  const res = await axios.post('/alliance/diplomacy/break', { walletAddress, targetAllianceId })
  return res.data
}

/** Leader: dispatch a character as envoy toward another alliance (travel time = hex distance × world march ms/hex). */
export const sendAlliancePeaceEnvoy = async (
  walletAddress: string,
  targetAllianceId: string,
  characterInstanceId: string,
) => {
  const res = await axios.post('/alliance/diplomacy/envoy', { walletAddress, targetAllianceId, characterInstanceId })
  return res.data
}

/** Leader (target): accept | reject | demand_tribute (with tributePoints when demanding). */
export const respondAlliancePeaceMission = async (
  walletAddress: string,
  missionId: string,
  action: 'accept' | 'reject' | 'demand_tribute',
  tributePoints?: number,
) => {
  const res = await axios.post('/alliance/diplomacy/mission/respond', {
    walletAddress,
    missionId,
    action,
    tributePoints,
  })
  return res.data
}

/** Leader (sender): send treasury resources matching demanded tribute points while mission is tribute_pending. */
export const fulfillAllianceTributeMission = async (
  walletAddress: string,
  missionId: string,
  resources: Partial<AllianceResources>,
) => {
  const res = await axios.post('/alliance/diplomacy/mission/tribute', { walletAddress, missionId, resources })
  return res.data
}

/** Leader (receiver): after tribute caravan arrives — accept (credit treasury + relation) or refuse (return goods to sender). */
export const respondAllianceTributeArrival = async (
  walletAddress: string,
  missionId: string,
  action: 'accept' | 'refuse',
) => {
  const res = await axios.post('/alliance/diplomacy/mission/tribute-arrival', {
    walletAddress,
    missionId,
    action,
  })
  return res.data
}

